import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, TextField, CircularProgress, Alert,
  Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Collapse
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AddIcon from '@mui/icons-material/Add';
import SeedIcon from '@mui/icons-material/PlaylistAdd';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '../api';
import { useEsMovil } from '../estilos/responsivo';
import { BLOQUE, CONTENEDOR_TABLA } from '../estilos/tarjeta';

const tipoColor = {
  activo: { bg: '#dbeafe', color: '#2563eb' },
  pasivo: { bg: '#fce7f3', color: '#db2777' },
  patrimonio: { bg: '#f3e8ff', color: '#7c3aed' },
  ingreso: { bg: '#d1fae5', color: '#059669' },
  gasto: { bg: '#fee2e2', color: '#dc2626' },
  costo: { bg: '#ffedd5', color: '#ea580c' },
};

function CuentaRow({ cuenta, cuentas, level = 0, onEdit }) {
  const [open, setOpen] = useState(level < 2);
  const hijos = cuentas.filter(c => c.padre_id === cuenta.id);
  const hasChildren = hijos.length > 0;
  const tc = tipoColor[cuenta.tipo] || { bg: '#f1f5f9', color: '#64748b' };

  return (
    <>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, py: 0.75, px: 1.5,
        pl: 1.5 + level * 3, borderBottom: '1px solid #f1f5f9',
        '&:hover': { bgcolor: '#f8fafc' }, transition: 'background 0.15s',
        opacity: cuenta.activa ? 1 : 0.5,
      }}>
        {hasChildren ? (
          <IconButton size="small" onClick={() => setOpen(!open)} sx={{ p: 0.25 }}>
            {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
        ) : (
          <Box sx={{ width: 24 }} />
        )}
        <Typography sx={{
          fontWeight: cuenta.nivel <= 2 ? 700 : 400, fontSize: 13, color: '#1e293b',
          fontFamily: 'monospace', minWidth: 60,
        }}>
          {cuenta.codigo}
        </Typography>
        <Typography sx={{
          flex: 1, fontSize: 13, fontWeight: cuenta.nivel === 1 ? 700 : cuenta.nivel === 2 ? 600 : 400,
          color: cuenta.nivel === 1 ? '#1e293b' : '#334155',
          textTransform: cuenta.nivel === 1 ? 'uppercase' : 'none',
        }}>
          {cuenta.nombre}
        </Typography>
        <Chip label={cuenta.tipo} size="small" sx={{
          height: 20, fontSize: 10, fontWeight: 600, bgcolor: tc.bg, color: tc.color,
        }} />
        {cuenta.clasificacion_niif && (
          <Chip label={cuenta.clasificacion_niif === 'corriente' ? 'Corriente' : 'No Corriente'} size="small"
            variant="outlined" sx={{ height: 20, fontSize: 10, fontWeight: 500 }} />
        )}
        <Chip label={cuenta.naturaleza} size="small" variant="outlined" sx={{
          height: 20, fontSize: 10, color: cuenta.naturaleza === 'debito' ? '#2563eb' : '#059669',
          borderColor: cuenta.naturaleza === 'debito' ? '#93c5fd' : '#86efac',
        }} />
        <Tooltip title="Editar">
          <IconButton size="small" onClick={() => onEdit(cuenta)} sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      {hasChildren && (
        <Collapse in={open}>
          {hijos.map(h => (
            <CuentaRow key={h.id} cuenta={h} cuentas={cuentas} level={level + 1} onEdit={onEdit} />
          ))}
        </Collapse>
      )}
    </>
  );
}

export default function PlanCuentas() {
  const esMovil = useEsMovil();
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [msg, setMsg] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editCuenta, setEditCuenta] = useState(null);
  const [form, setForm] = useState({ codigo: '', nombre: '', tipo: 'activo', naturaleza: 'debito', clasificacion_niif: '', padre_id: '', nivel: 3 });

  const fetchCuentas = async () => {
    setLoading(true);
    try {
      const res = await api.get('/contabilidad/cuentas');
      setCuentas(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCuentas(); }, []);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await api.post('/contabilidad/cuentas/seed');
      setMsg({ type: 'success', text: `Plan de cuentas NIIF cargado: ${res.data.cuentas_creadas} cuentas creadas` });
      fetchCuentas();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.detail || 'Error al cargar plan de cuentas' });
    } finally { setSeeding(false); }
  };

  const handleOpenCreate = () => {
    setEditCuenta(null);
    setForm({ codigo: '', nombre: '', tipo: 'activo', naturaleza: 'debito', clasificacion_niif: '', padre_id: '', nivel: 3 });
    setDialogOpen(true);
  };

  const handleOpenEdit = (cuenta) => {
    setEditCuenta(cuenta);
    setForm({ nombre: cuenta.nombre, clasificacion_niif: cuenta.clasificacion_niif || '', activa: cuenta.activa });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editCuenta) {
        await api.put(`/contabilidad/cuentas/${editCuenta.id}`, {
          nombre: form.nombre,
          clasificacion_niif: form.clasificacion_niif || null,
          activa: form.activa !== undefined ? form.activa : true,
        });
        setMsg({ type: 'success', text: 'Cuenta actualizada' });
      } else {
        await api.post('/contabilidad/cuentas', {
          codigo: form.codigo, nombre: form.nombre, tipo: form.tipo,
          naturaleza: form.naturaleza, nivel: parseInt(form.nivel),
          clasificacion_niif: form.clasificacion_niif || null,
          padre_id: form.padre_id ? parseInt(form.padre_id) : null,
        });
        setMsg({ type: 'success', text: 'Cuenta creada' });
      }
      setDialogOpen(false);
      fetchCuentas();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.detail || 'Error' });
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;

  const rootCuentas = cuentas.filter(c => !c.padre_id);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography sx={{ color: '#64748b', fontSize: 13 }}>
            {cuentas.length} cuentas | NIIF para PYMES
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Actualizar">
            <IconButton onClick={fetchCuentas}><RefreshIcon /></IconButton>
          </Tooltip>
          {cuentas.length === 0 && (
            <Button variant="contained" startIcon={<SeedIcon />} onClick={handleSeed} disabled={seeding}
              sx={{ bgcolor: '#7c3aed', borderRadius: 1, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#6d28d9' } }}>
              {seeding ? 'Cargando...' : 'Cargar Plan NIIF'}
            </Button>
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}
            sx={{ bgcolor: '#1e3a5f', borderRadius: 1, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#2d5a87' } }}>
            Nueva Cuenta
          </Button>
        </Box>
      </Box>

      {msg && <Alert severity={msg.type} onClose={() => setMsg(null)} sx={{ mb: 2, borderRadius: 1 }}>{msg.text}</Alert>}

      {cuentas.length === 0 ? (
        <Paper elevation={0} sx={{ ...BLOQUE, py: 4, textAlign: 'center' }}>
          <AccountTreeIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1.5 }} />
          <Typography sx={{ color: '#1e293b', fontSize: 16, fontWeight: 600 }}>Plan de cuentas vacio</Typography>
          <Typography sx={{ color: '#64748b', fontSize: 13, mb: 2 }}>
            Carga el catalogo NIIF para PYMES con un clic
          </Typography>
        </Paper>
      ) : (
        <Paper elevation={0} sx={CONTENEDOR_TABLA}>
          {/* Header */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
            bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0',
          }}>
            <Box sx={{ width: 24 }} />
            <Typography sx={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 600, color: '#64748b', minWidth: 60 }}>Codigo</Typography>
            <Typography sx={{ flex: 1, fontSize: 11, fontWeight: 600, color: '#64748b' }}>Nombre</Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b', width: 60, textAlign: 'center' }}>Tipo</Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b', width: 80, textAlign: 'center' }}>NIIF</Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b', width: 60, textAlign: 'center' }}>Nat.</Typography>
            <Box sx={{ width: 32 }} />
          </Box>
          {rootCuentas.map(c => (
            <CuentaRow key={c.id} cuenta={c} cuentas={cuentas} onEdit={handleOpenEdit} />
          ))}
        </Paper>
      )}

      {/* Dialog crear/editar */}
      <Dialog fullScreen={esMovil} open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle>
          {editCuenta ? 'Editar Cuenta' : 'Nueva Cuenta'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {!editCuenta && (
              <>
                <TextField label="Codigo" value={form.codigo} onChange={e => setForm({ ...form, codigo: e.target.value })}
                  size="small" placeholder="ej: 1110" helperText="Codigo unico de la cuenta" />
                <TextField label="Tipo" value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })}
                  size="small" select>
                  {['activo', 'pasivo', 'patrimonio', 'ingreso', 'gasto', 'costo'].map(t => (
                    <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>
                  ))}
                </TextField>
                <TextField label="Naturaleza" value={form.naturaleza} onChange={e => setForm({ ...form, naturaleza: e.target.value })}
                  size="small" select>
                  <MenuItem value="debito">Debito</MenuItem>
                  <MenuItem value="credito">Credito</MenuItem>
                </TextField>
                <TextField label="Nivel" value={form.nivel} onChange={e => setForm({ ...form, nivel: e.target.value })}
                  size="small" select>
                  {[1, 2, 3, 4].map(n => (
                    <MenuItem key={n} value={n}>Nivel {n} — {['Clase', 'Grupo', 'Cuenta', 'Subcuenta'][n - 1]}</MenuItem>
                  ))}
                </TextField>
                <TextField label="Cuenta Padre (ID)" value={form.padre_id}
                  onChange={e => setForm({ ...form, padre_id: e.target.value })}
                  size="small" type="number" helperText="Opcional. ID de la cuenta padre" />
              </>
            )}
            <TextField label="Nombre" value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })}
              size="small" />
            <TextField label="Clasificacion NIIF" value={form.clasificacion_niif}
              onChange={e => setForm({ ...form, clasificacion_niif: e.target.value })}
              size="small" select>
              <MenuItem value="">Sin clasificacion</MenuItem>
              <MenuItem value="corriente">Corriente</MenuItem>
              <MenuItem value="no_corriente">No Corriente</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: '#64748b' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}
            sx={{ bgcolor: '#1e3a5f', textTransform: 'none', fontWeight: 600, borderRadius: 1, '&:hover': { bgcolor: '#2d5a87' } }}>
            {editCuenta ? 'Guardar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
